import {
  AHSeparator,
  AHTeamIcon,
  AHTeamIconCircle,
  Box,
  resolveAHTextStyle,
  Space,
  useBoxStyle,
  useLayoutContext,
  useScaleFactor,
  useTheme,
} from "arkh";
import { TextInput } from "react-native";
import { useIntl } from "react-intl";
import React, { useCallback, useMemo, useState } from "react";

import { Option } from "./Option";
import { getTeamColorMatches, teamColors } from "./teamColors";
import { getTeamIconSuggestions, TeamIcon, teamIcons } from "./teamIcons";

import { useSafeAreaInsets } from "react-native-safe-area-context";
import { OptionSelect } from "../../components/mobile/OptionSelect";
import { KeyboardAwareScrollView } from "react-native-keyboard-controller";

type Props = {
  color: string;
  icon: TeamIcon;
  name: string;
  isPrivate: boolean;
  usedColors: string[];
  usedIcons: TeamIcon[];
  setColor: (color: string) => void;
  setName: (name: string) => void;
  setIcon: (icon: TeamIcon) => void;
  mode: "create" | "edit";
  LockOption: React.ReactNode;
};

function id<T>(a: T): T {
  return a;
}

export function TeamIdentifiersComponent(props: Props) {
  const usedSectionTitle = useUsedSectionTitle();

  const insets = useSafeAreaInsets();
  const intl = useIntl();
  const sf = useScaleFactor();
  const layout = useLayoutContext();
  const theme = useTheme();
  const contentContainerStyle = useBoxStyle({
    color: "surface",
  });
  const [hasSetColor, setHasSetColor] = useState(false);
  const [hasSetIcon, setHasSetIcon] = useState(false);
  const _setIcon = useCallback(
    (icon: TeamIcon) => {
      if (!hasSetIcon) {
        setHasSetIcon(true);
      }
      props.setIcon(icon);
    },
    [hasSetIcon, props]
  );
  const _setColor = useCallback(
    (color: string) => {
      if (!hasSetColor) {
        setHasSetColor(true);
      }
      props.setColor(color);
    },
    [hasSetColor, props]
  );
  const onChangeText = useCallback(
    (text: string) => {
      props.setName(text);
      if (props.mode === "create") {
        if (!hasSetColor) {
          const matchingColors = getTeamColorMatches(text, props.usedColors);
          if (matchingColors.length > 0) {
            props.setColor(matchingColors[0]);
          }
        }
        if (!hasSetIcon) {
          const matchingIcons = getTeamIconSuggestions(text, props.usedIcons);
          if (matchingIcons.length > 0) {
            return props.setIcon(matchingIcons[0]);
          }
        }
      }
    },
    [hasSetColor, hasSetIcon, props]
  );
  return (
    <KeyboardAwareScrollView
      style={{
        flex: 1,
      }}
      keyboardShouldPersistTaps="handled"
      contentContainerStyle={[
        contentContainerStyle,
        {
          paddingBottom: layout.space.large + insets.bottom,
          paddingTop: layout.space.medium,
        },
      ]}
    >
      <Box style={{ alignSelf: "center" }}>
        <AHTeamIconCircle
          type={props.icon}
          color={props.color}
          size="large"
          isPrivate={props.isPrivate}
        />
      </Box>
      <Box spaceBottom="large" />
      <Box
        spaceHorizontal="medium"
        spaceBottom="large"
        style={{ flexDirection: "row", justifyContent: "center" }}
      >
        <Box style={{ maxWidth: 400, flex: 1 }}>
          <Box
            color="background"
            cornerRadius="large"
            spaceHorizontal="medium"
            spaceVertical="medium"
          >
            <TextInput
              placeholder={intl.formatMessage({
                defaultMessage: "Team Name",
                description: "Team Name input placeholder",
              })}
              placeholderTextColor={theme.washedText}
              autoFocus={props.mode === "create"}
              autoCorrect={false}
              autoComplete="off"
              style={[
                resolveAHTextStyle("title", sf, theme, layout),
                {
                  flex: 1,
                  textAlign: "center",
                  flexDirection: "row",
                  fontWeight: "normal",
                },
              ]}
              value={props.name}
              returnKeyType="done"
              onChangeText={onChangeText}
            />
          </Box>
        </Box>
      </Box>
      {props.LockOption}
      <AHSeparator />
      <IconSelect
        selectedIcon={props.icon}
        setSelectedIcon={_setIcon}
        usedIcons={props.usedIcons}
      />
      <AHSeparator />
      <ColorSelect
        spaceHorizontal="medium"
        palette={teamColors}
        usedSectionTitle={usedSectionTitle}
        usedColors={props.usedColors}
        selectedColor={props.color}
        setSelectedColor={_setColor}
      />
    </KeyboardAwareScrollView>
  );
}

function useUsedSectionTitle() {
  const intl = useIntl();
  return intl.formatMessage({
    defaultMessage: "Used by other teams",
    description:
      "TeamIdentifiers option select colors/icons used by other teams section header",
  });
}

export function ColorSelect(props: {
  usedColors: string[];
  usedSectionTitle: string;
  selectedColor: string;
  palette: string[];
  setSelectedColor: (color: string) => void;
  spaceHorizontal: Space;
}) {
  const unusedColors = useMemo(
    () => props.palette.filter((color) => !props.usedColors.includes(color)),
    [props.palette, props.usedColors]
  );

  const renderItem = useCallback(
    (color: string) => (
      <ColorOption
        key={color}
        color={color}
        selected={color === props.selectedColor}
        setSelectedColor={props.setSelectedColor}
      />
    ),
    [props.selectedColor, props.setSelectedColor]
  );

  return (
    <OptionSelect
      keyExtractor={id}
      usedSectionTitle={props.usedSectionTitle}
      unused={unusedColors}
      used={props.usedColors}
      renderItem={renderItem}
      spaceHorizontal={props.spaceHorizontal}
    />
  );
}

function ColorOption({
  color,
  selected,
  setSelectedColor,
}: {
  color: string;
  selected: boolean;
  setSelectedColor: (color: string) => void;
}) {
  return (
    <Option
      selected={selected}
      color={color}
      onPress={() => {
        setSelectedColor(color);
      }}
    />
  );
}

function IconSelect(props: {
  usedIcons: TeamIcon[];
  selectedIcon: TeamIcon;
  setSelectedIcon: (icon: TeamIcon) => void;
}) {
  const unusedSectionTitle = useUsedSectionTitle();
  const unusedIcons = useMemo(
    () => teamIcons.filter((icon) => !props.usedIcons.includes(icon)),
    [props.usedIcons]
  );

  const renderItem = useCallback(
    (icon: TeamIcon) => (
      <IconOption
        key={icon}
        setSelectedIcon={props.setSelectedIcon}
        iconType={icon}
        selected={icon === props.selectedIcon}
      />
    ),
    [props.selectedIcon, props.setSelectedIcon]
  );
  return (
    <OptionSelect
      spaceHorizontal="medium"
      keyExtractor={id}
      usedSectionTitle={unusedSectionTitle}
      unused={unusedIcons}
      used={props.usedIcons}
      renderItem={renderItem}
    />
  );
}

function IconOption({
  selected,
  setSelectedIcon,
  iconType,
}: {
  iconType: TeamIcon;
  selected: boolean;
  setSelectedIcon: (icon: TeamIcon) => void;
}) {
  const theme = useTheme();
  return (
    <Option
      onPress={() => {
        setSelectedIcon(iconType);
      }}
      color={theme.background}
      selected={selected}
    >
      <AHTeamIcon type={iconType} color="darkerText" />
    </Option>
  );
}
